import { trim } from 'lodash'
import React, { useEffect, useState } from 'react'
import { Controller, useForm, SubmitHandler } from 'react-hook-form/dist/index.ie11'
import { useTranslation } from '../i18n'
import { isValidEmail, getLatestCookie } from '../utils/auth'
import Button from './Button'
import Checkbox from './Checkbox'
import Input from './Input'
import LinkButton from './LinkButton'

export type LoginFormValues = {
  email: string
  password: string
  isRemember: boolean
}

interface Props {
  isSnsLoginOnRequest?: boolean
  isLoading?: boolean
  useRememberMe?: boolean
  onSubmit: SubmitHandler<LoginFormValues>
}

const MINIMUM_PASSWORD_LENGTH = 5
const TRIED_EMAIL = 'tried_email'

const defaultValues: LoginFormValues = {
  email: '',
  password: '',
  isRemember: false,
}

export default function LoginForm({
  isSnsLoginOnRequest = false,
  isLoading = false,
  useRememberMe = true,
  onSubmit,
}: Props) {
  const { t } = useTranslation()
  const [showPassword, setShowPassword] = useState(false)
  const { register, handleSubmit, control, errors, setValue } = useForm<LoginFormValues>({
    mode: 'all',
    defaultValues,
    shouldFocusError: false,
  })

  useEffect(() => {
    if (getLatestCookie(TRIED_EMAIL)) {
      const triedEmail = trim(getLatestCookie(TRIED_EMAIL), '"')
      setValue('email', triedEmail, { shouldValidate: true })
      setValue('isRemember', !!triedEmail)
    }
  }, [])

  return (
    <form className="LoginForm" onSubmit={handleSubmit(onSubmit)} data-testid="form">
      <Input
        ref={register({
          required: t('errors.emailBlank').toString(),
          validate: (email: LoginFormValues['email']) => isValidEmail(email) || t('errors.invalidEmail').toString(),
        })}
        name="email"
        type="email"
        label={t('login.email')}
        error={!!errors.email}
        errorMessage={errors.email && errors.email.message}
        disabled={isLoading || isSnsLoginOnRequest}
        autoComplete="off"
        testId="email"
      />
      <Input
        ref={register({
          required: t('login.error.password1').toString(),
          minLength: {
            value: MINIMUM_PASSWORD_LENGTH,
            message: t('login.error.password2'),
          },
        })}
        className="LoginForm__passwordInput"
        name="password"
        type={showPassword ? 'text' : 'password'}
        label={t('login.password')}
        error={!!errors.password}
        errorMessage={errors.password && errors.password.message}
        disabled={isLoading || isSnsLoginOnRequest}
        testId="password"
        rightEl={
          showPassword ? (
            <Button
              theme="iconOnly"
              iconType="FunPwHidden"
              size="small"
              title={t('login.hidepassword')}
              onClick={() => setShowPassword(false)}
            />
          ) : (
            <Button
              theme="iconOnly"
              iconType="FunPwShow"
              size="small"
              title={t('login.showpassword')}
              onClick={() => setShowPassword(true)}
            />
          )
        }
      />
      <div className="LoginForm__options">
        <div>
          {useRememberMe && (
            <Controller
              control={control}
              name="isRemember"
              render={({ onChange, onBlur, value, name }) => (
                <Checkbox
                  label={t('login.remember')}
                  name={name}
                  checked={value}
                  onBlur={onBlur}
                  onChange={e => onChange(e.target.checked)}
                  disabled={isLoading || isSnsLoginOnRequest}
                  testId="isRemember"
                />
              )}
            />
          )}
        </div>
        {/* TODO: 프론트서버 reset-password 페이지 배포할 때, external prop 제거 */}
        <LinkButton
          className="LoginForm__forgotPassword"
          title={t('login.forgot')}
          theme="text"
          size="medium"
          color="green"
          testId="resetPasswordLink"
          to="/includes15/reset-password"
          external
        />
      </div>
      <Button
        className="LoginForm__button"
        type="submit"
        title={t('login.loginButtonText')}
        isLoading={isLoading}
        disabled={isLoading || isSnsLoginOnRequest}
        fullWidth
        testId="submit"
      />
    </form>
  )
}
